import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";

/* NC500 launch — when we're within LAUNCH_OVERRIDE_DAYS the entire site's
 * PageCTA switches to the "back the expedition" variant automatically. */
const NC500_LAUNCH = new Date("2026-11-06T06:00:00Z");
const LAUNCH_OVERRIDE_DAYS = 30;

function useLaunchOverride() {
    const [override, setOverride] = useState(false);
    useEffect(() => {
        const evaluate = () => {
            const diff = NC500_LAUNCH.getTime() - Date.now();
            const days = diff / 86400000;
            // Active from 30 days out through Day +7 of the mission
            setOverride(days <= LAUNCH_OVERRIDE_DAYS && days >= -7);
        };
        evaluate();
        const id = setInterval(evaluate, 60_000); // check once a minute
        return () => clearInterval(id);
    }, []);
    return override;
}

/**
 * Reusable page-ending call-to-action.
 *
 * Presets keep copy consistent across the site. Use `variant` for a quick preset,
 * or pass `headline` / `sub` / `primary` / `secondary` to fully customise.
 *
 * Variants:
 *  - flight  → "The next flight is one click away."  → Watch · Read the story
 *  - story   → "Every waypoint has a story."          → Read the story · Watch
 *  - journey → "Join the journey. Scotland from the wind." → Subscribe · Book
 *  - book    → "Bring your idea to the sky."         → Book a shoot · See past flights
 *  - back    → "Fuel the flight."                    → Back the expedition · Watch
 */
const PRESETS = {
    flight: {
        overline: "One more waypoint",
        headline: (
            <>The next flight<br /><span className="italic text-[var(--sb-brand)]">is one click away.</span></>
        ),
        sub: "Everything I&rsquo;ve filmed from four hundred feet — in one place, tap to play.",
        primary: { label: "Watch the latest flight", to: "/gallery" },
        secondary: { label: "Read the story", to: "/start-here" },
    },
    story: {
        overline: "Behind every frame",
        headline: (
            <>Every waypoint<br /><span className="italic text-[var(--sb-brand)]">has a story.</span></>
        ),
        sub: "The journey behind the drone — reconstruction, resilience, and the sky above Scotland.",
        primary: { label: "Read the story", to: "/start-here" },
        secondary: { label: "See the flights", to: "/gallery" },
    },
    journey: {
        overline: "Come along",
        headline: (
            <>Join the journey.<br /><span className="italic text-[var(--sb-brand)]">Where my spirit takes flight.</span></>
        ),
        sub: "Sign in to unlock the full archive, follow the mission log, and get pilot-side news first.",
        primary: { label: "Join the journey", to: "/subscription" },
        secondary: { label: "Book a shoot", to: "/book" },
    },
    book: {
        overline: "Charter · Fly",
        headline: (
            <>Bring your idea<br /><span className="italic text-[var(--sb-brand)]">to the sky.</span></>
        ),
        sub: "Pick a date, a location on the map, and a time. Availability updates in real time.",
        primary: { label: "Book a shoot", to: "/book" },
        secondary: { label: "See past flights", to: "/gallery" },
    },
    back: {
        overline: "The NC500 Mission",
        headline: (
            <>Fuel the flight.<br /><span className="italic text-[var(--sb-brand)]">Fund the mission.</span></>
        ),
        sub: "Every mile of the 516-mile NC500 expedition raises funds for the MS Society.",
        primary: { label: "Back the expedition", to: "https://gofund.me/da1365285", external: true },
        secondary: { label: "Read the pilot's log", to: "/pilots-log" },
    },
};

export default function PageCTA({
    variant = "flight",
    headline,
    sub,
    overline,
    primary,
    secondary,
    className = "",
    forceVariant = false, // when true, the launch override is bypassed
}) {
    const launchOverride = useLaunchOverride();
    const activeVariant = !forceVariant && launchOverride ? "back" : variant;
    const p = PRESETS[activeVariant] || PRESETS.flight;
    const _headline = headline ?? p.headline;
    const _sub = sub ?? p.sub;
    const _overline = overline ?? p.overline;
    const _primary = primary ?? p.primary;
    const _secondary = secondary ?? p.secondary;

    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-15% 0px" });

    const renderCTA = (cta, isPrimary) => {
        if (!cta) return null;
        const cls = isPrimary ? "btn-primary" : "btn-outline";
        const dtid = `page-cta-${isPrimary ? "primary" : "secondary"}`;
        const icon = <ArrowRight size={16} />;
        if (cta.external) {
            return (
                <a href={cta.to} target="_blank" rel="noreferrer" className={cls} data-testid={dtid}>
                    {cta.label} {icon}
                </a>
            );
        }
        return (
            <Link to={cta.to} className={cls} data-testid={dtid}>
                {cta.label} {icon}
            </Link>
        );
    };

    return (
        <section
            ref={ref}
            className={`relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 border-t border-[var(--sb-border)] ${className}`}
            data-testid="page-cta"
            data-variant={activeVariant}
            data-launch-override={launchOverride ? "true" : "false"}
        >
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none opacity-[0.5]"
                style={{
                    background: "radial-gradient(50% 60% at 20% 40%, rgba(107,167,222,0.12) 0%, rgba(0,0,0,0) 60%)",
                }}
            />
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end"
            >
                <div className="lg:col-span-8">
                    <div className="overline mb-4">{_overline}</div>
                    <h2
                        className="font-serif tracking-tight leading-[0.98]"
                        style={{ fontSize: "clamp(2.2rem, 5vw, 4.4rem)" }}
                    >
                        {_headline}
                    </h2>
                    {_sub && (
                        <p className="mt-6 text-[var(--sb-text-dim)] text-lg leading-relaxed max-w-xl">
                            {_sub.replace(/&rsquo;/g, "\u2019")}
                        </p>
                    )}
                </div>
                <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
                    {renderCTA(_primary, true)}
                    {renderCTA(_secondary, false)}
                </div>
            </motion.div>
        </section>
    );
}
